import type {
  Appointment,
  AppointmentFormData,
  AppointmentWithDetails,
  AgendaFilters,
  AppointmentFilters,
  CalendarEvent,
  BlockedPeriod,
  AppointmentStatus,
  PaginatedResponse,
} from '@leoclinic/types';
import { getSupabaseClient } from '../supabase/client';

const STATUS_COLORS: Record<AppointmentStatus, string> = {
  pending: '#C7A567',
  confirmed: '#2F4F46',
  in_progress: '#3d6b5f',
  completed: '#6b7280',
  cancelled: '#ef4444',
  no_show: '#f97316',
};

/**
 * Serviço de marcações — Repository Pattern
 */
export class AppointmentService {
  /**
   * Lista marcações num intervalo de datas
   */
  async list(clinicId: string, filters: AgendaFilters): Promise<AppointmentWithDetails[]> {
    const supabase = getSupabaseClient();

    let query = supabase
      .from('appointments')
      .select('*')
      .eq('clinic_id', clinicId)
      .gte('start_at', filters.start)
      .lte('start_at', filters.end)
      .neq('status', 'cancelled');

    if (filters.therapist_id) {
      query = query.eq('therapist_id', filters.therapist_id);
    }

    const { data, error } = await query.order('start_at');
    if (error) throw error;

    return this.enrichAppointments(data ?? []);
  }

  async listPaginated(
    clinicId: string,
    filters: AppointmentFilters = {},
  ): Promise<PaginatedResponse<AppointmentWithDetails>> {
    const supabase = getSupabaseClient();
    const page = filters.page ?? 1;
    const pageSize = filters.pageSize ?? 20;

    let query = supabase
      .from('appointments')
      .select('*')
      .eq('clinic_id', clinicId)
      .order('start_at', { ascending: false });

    if (filters.status) query = query.eq('status', filters.status);
    if (filters.therapist_id) query = query.eq('therapist_id', filters.therapist_id);
    if (filters.from) query = query.gte('start_at', filters.from);
    if (filters.to) query = query.lte('start_at', filters.to);

    const { data, error } = await query;
    if (error) throw error;

    const rows = data ?? [];
    const enriched = await this.enrichAppointments(rows);

    if (filters.query?.trim()) {
      const q = filters.query.trim().toLowerCase();
      const filtered = enriched.filter(
        (a) =>
          a.client_name.toLowerCase().includes(q) ||
          a.treatment_name.toLowerCase().includes(q) ||
          a.therapist_name.toLowerCase().includes(q),
      );
      const total = filtered.length;
      const from = (page - 1) * pageSize;
      return {
        data: filtered.slice(from, from + pageSize),
        total,
        page,
        pageSize,
        totalPages: Math.ceil(total / pageSize) || 1,
      };
    }

    const total = enriched.length;
    const from = (page - 1) * pageSize;
    return {
      data: enriched.slice(from, from + pageSize),
      total,
      page,
      pageSize,
      totalPages: Math.ceil(total / pageSize) || 1,
    };
  }

  async updateStatus(id: string, status: AppointmentStatus): Promise<AppointmentWithDetails> {
    return this.update(id, { status });
  }

  async confirm(id: string): Promise<AppointmentWithDetails> {
    return this.updateStatus(id, 'confirmed');
  }

  async start(id: string): Promise<AppointmentWithDetails> {
    return this.updateStatus(id, 'in_progress');
  }

  async complete(id: string): Promise<AppointmentWithDetails> {
    return this.updateStatus(id, 'completed');
  }

  async getRecent(clinicId: string, limit = 5): Promise<AppointmentWithDetails[]> {
    const supabase = getSupabaseClient();
    const { data, error } = await supabase
      .from('appointments')
      .select('*')
      .eq('clinic_id', clinicId)
      .neq('status', 'cancelled')
      .order('start_at', { ascending: false })
      .limit(limit);
    if (error) throw error;
    return this.enrichAppointments(data ?? []);
  }

  async getById(id: string): Promise<AppointmentWithDetails | null> {
    const supabase = getSupabaseClient();
    const { data, error } = await supabase.from('appointments').select('*').eq('id', id).single();
    if (error) {
      if (error.code === 'PGRST116') return null;
      throw error;
    }
    const enriched = await this.enrichAppointments([data]);
    return enriched[0] ?? null;
  }

  async create(clinicId: string, form: AppointmentFormData): Promise<AppointmentWithDetails> {
    const supabase = getSupabaseClient();
    const { data, error } = await supabase
      .from('appointments')
      .insert({
        clinic_id: clinicId,
        client_id: form.client_id,
        therapist_id: form.therapist_id,
        treatment_id: form.treatment_id,
        start_at: form.start_at,
        end_at: form.end_at,
        status: form.status,
        notes: form.notes || null,
      })
      .select()
      .single();

    if (error) throw error;
    const enriched = await this.enrichAppointments([data]);
    return enriched[0];
  }

  async update(id: string, form: Partial<AppointmentFormData>): Promise<AppointmentWithDetails> {
    const supabase = getSupabaseClient();
    const { data, error } = await supabase
      .from('appointments')
      .update({
        ...(form.client_id !== undefined && { client_id: form.client_id }),
        ...(form.therapist_id !== undefined && { therapist_id: form.therapist_id }),
        ...(form.treatment_id !== undefined && { treatment_id: form.treatment_id }),
        ...(form.start_at !== undefined && { start_at: form.start_at }),
        ...(form.end_at !== undefined && { end_at: form.end_at }),
        ...(form.status !== undefined && { status: form.status }),
        ...(form.notes !== undefined && { notes: form.notes || null }),
      })
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;
    const enriched = await this.enrichAppointments([data]);
    return enriched[0];
  }

  async updateSchedule(id: string, start_at: string, end_at: string): Promise<AppointmentWithDetails> {
    return this.update(id, { start_at, end_at });
  }

  async cancel(id: string): Promise<void> {
    const supabase = getSupabaseClient();
    const { error } = await supabase
      .from('appointments')
      .update({ status: 'cancelled' })
      .eq('id', id);
    if (error) throw error;
  }

  async duplicate(id: string): Promise<AppointmentWithDetails> {
    const original = await this.getById(id);
    if (!original) throw new Error('Marcação não encontrada');

    const duration = new Date(original.end_at).getTime() - new Date(original.start_at).getTime();
    const newStart = new Date(original.start_at);
    newStart.setDate(newStart.getDate() + 7);
    const newEnd = new Date(newStart.getTime() + duration);

    return this.create(original.clinic_id, {
      client_id: original.client_id,
      therapist_id: original.therapist_id,
      treatment_id: original.treatment_id,
      start_at: newStart.toISOString(),
      end_at: newEnd.toISOString(),
      status: 'pending',
      notes: original.notes,
    });
  }

  /**
   * Obtém bloqueios como eventos de fundo
   */
  async getBlockedEvents(
    clinicId: string,
    filters: AgendaFilters,
  ): Promise<CalendarEvent[]> {
    const supabase = getSupabaseClient();

    let query = supabase
      .from('blocked_periods')
      .select('*')
      .eq('clinic_id', clinicId)
      .gte('end_at', filters.start)
      .lte('start_at', filters.end);

    if (filters.therapist_id) {
      query = query.eq('therapist_id', filters.therapist_id);
    }

    const { data, error } = await query;
    if (error) throw error;

    return (data as BlockedPeriod[]).map((bp) => ({
      id: `blocked-${bp.id}`,
      title: bp.reason ?? 'Bloqueado',
      start: bp.start_at,
      end: bp.end_at,
      backgroundColor: '#ECECEC',
      borderColor: '#d1d5db',
      textColor: '#6b7280',
      display: 'background',
      extendedProps: {
        appointment: {} as AppointmentWithDetails,
        isBlocked: true,
      },
    }));
  }

  /**
   * Converte marcações para eventos FullCalendar
   */
  toCalendarEvents(appointments: AppointmentWithDetails[]): CalendarEvent[] {
    return appointments.map((apt) => ({
      id: apt.id,
      title: `${apt.client_name} — ${apt.treatment_name}`,
      start: apt.start_at,
      end: apt.end_at,
      backgroundColor: apt.therapist_color ?? STATUS_COLORS[apt.status],
      borderColor: apt.therapist_color ?? STATUS_COLORS[apt.status],
      textColor: '#FFFFFF',
      extendedProps: { appointment: apt },
    }));
  }

  private async enrichAppointments(rows: Appointment[]): Promise<AppointmentWithDetails[]> {
    if (!rows.length) return [];

    const supabase = getSupabaseClient();
    const clientIds = [...new Set(rows.map((r) => r.client_id))];
    const treatmentIds = [...new Set(rows.map((r) => r.treatment_id))];
    const therapistIds = [...new Set(rows.map((r) => r.therapist_id))];

    const [{ data: clients }, { data: treatments }, { data: therapists }] = await Promise.all([
      supabase.from('clients').select('id, full_name').in('id', clientIds),
      supabase.from('treatments').select('id, name, duration_minutes, color').in('id', treatmentIds),
      supabase.from('therapists').select('id, color, employee_id').in('id', therapistIds),
    ]);

    const employeeIds = [...new Set((therapists ?? []).map((t) => t.employee_id))];
    const { data: employees } = employeeIds.length
      ? await supabase.from('employees').select('id, profile_id').in('id', employeeIds)
      : { data: [] };

    const profileIds = [...new Set((employees ?? []).map((e) => e.profile_id))];
    const { data: profiles } = profileIds.length
      ? await supabase.from('profiles').select('id, full_name').in('id', profileIds)
      : { data: [] };

    const clientMap = new Map((clients ?? []).map((c) => [c.id, c.full_name]));
    const treatmentMap = new Map((treatments ?? []).map((t) => [t.id, t]));
    const therapistMap = new Map((therapists ?? []).map((t) => [t.id, t]));
    const employeeMap = new Map((employees ?? []).map((e) => [e.id, e.profile_id]));
    const profileMap = new Map((profiles ?? []).map((p) => [p.id, p.full_name]));

    return rows.map((row) => {
      const treatment = treatmentMap.get(row.treatment_id);
      const therapist = therapistMap.get(row.therapist_id);
      const profileId = therapist ? employeeMap.get(therapist.employee_id) : null;
      const therapistName = profileId ? profileMap.get(profileId) ?? '—' : '—';

      return {
        ...row,
        client_name: clientMap.get(row.client_id) ?? '—',
        treatment_name: treatment?.name ?? '—',
        treatment_duration: treatment?.duration_minutes ?? 60,
        therapist_name: therapistName,
        therapist_color: therapist?.color ?? treatment?.color ?? null,
        treatment_color: treatment?.color ?? null,
      };
    });
  }
}

export const appointmentService = new AppointmentService();
